import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
  PipeTransform,
  Pipe,
  Renderer2,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms'; // Import ReactiveFormsModule
import { CredentialsService } from '../../services/credentials.service';
import { CpfSharedService } from '../../services/cpf-shared.service';
import { CredentialsStateService } from '../../services/credentials-state.service';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Subject, takeUntil, finalize } from 'rxjs';
import { NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import { CommonModule, DatePipe } from '@angular/common';
import { Clipboard } from '@angular/cdk/clipboard';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { InputMaskModule } from 'primeng/inputmask';
import { PanelModule } from 'primeng/panel';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';

@Pipe({
  name: 'partialCpfMask',
  standalone: true
})
export class PartialCpfMaskPipe implements PipeTransform {
  transform(
    value: string,
    visibleStart: number = 3,
    visibleEnd: number = 2
  ): string {
    if (!value || value.length !== 11) {
      return value;
    }

    const maskedPart = '#'.repeat(6); // 6 caracteres mascarados (###.###)
    return `${value.substring(0, visibleStart)}.${maskedPart.substring(
      0,
      3
    )}.${maskedPart.substring(3)}-${value.substring(11 - visibleEnd)}`;
  }
}

interface CredentialForm {
  credentialNumber: FormControl<string | null>;
}

interface Credential {
  credentialNumber: string;
  processNumber: string;
  status: string;
  type: string;
  applicantSummary: {
    id: string;
    cpf: string;
    name: string;
  };
  recipientSummary: {
    id: string;
    cpf: string;
    name: string;
  };
  userSummary: {
    id: string;
    login: string;
    username: string;
  };
  expiringDate: string;
  observation: string;
  cancellationReason: string;
  cancellationDate: string;
  cancellationJustify: string;
  bulletimNumber: string;
  createdAt: string;
  deletedAt: string;
}

@Component({
  selector: 'app-credential',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgxMaskDirective,
    NgxMaskPipe,
    DatePipe,
    CommonModule,
    FormsModule,
    PartialCpfMaskPipe,
    CardModule,
    ButtonModule,
    InputTextModule,
    InputMaskModule,
    PanelModule,
    ToastModule,
    TooltipModule,
  ],
  template: `
    <p-toast></p-toast>
    <p-card>
      <ng-template pTemplate="header">
        <div class="card-header">
          <h2><i class="pi pi-id-card"></i> Consulta Credencial</h2>
        </div>
      </ng-template>

      <div class="credential-container">
        <form [formGroup]="credentialForm" (submit)="onSubmitCredentialNumber()" class="credential-form">

          <!-- Seção: Consulta -->
          <p-panel header="Dados da Credencial" [toggleable]="false" styleClass="compact-panel">
            <div class="form-grid">
              <div class="form-row">
                <div class="form-field">
                  <label for="credentialNumber" class="field-label">Número da Credencial*</label>
                  <input
                    #credentialNumber
                    id="credentialNumberInput"
                    pInputText
                    formControlName="credentialNumber"
                    mask="9999999/9999"
                    [ngClass]="{ 'p-invalid': isFieldInvalid('credentialNumber') }"
                    class="compact-input"
                    placeholder="0000000/0000"
                    (input)="onCredentialInput($event)">
                  <small *ngIf="isFieldInvalid('credentialNumber')" class="p-error">Número da credencial é obrigatório</small>
                </div>

                <div class="form-field">
                  <label class="field-label">&nbsp;</label>
                  <div class="button-group">
                    <button
                      type="submit"
                      class="p-button p-component search-button"
                      [disabled]="isLoading">
                      <i class="pi" [ngClass]="{'pi-spin pi-spinner': isLoading, 'pi-search': !isLoading}"></i>
                      <span class="p-button-label">Buscar</span>
                    </button>
                    <button
                      *ngIf="credentialForm.get('credentialNumber')?.value"
                      type="button"
                      class="clear-button"
                      (click)="clearSearch()"
                      title="Limpar busca">
                      <i class="pi pi-times"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </p-panel>

          <!-- Resultado da Consulta -->
          @if (credential) {
          <p-panel header="Dados da Credencial" [toggleable]="false" styleClass="compact-panel">
            <div class="credential-data">
              <div class="data-grid">
                <div class="data-row">
                  <div class="data-field">
                    <label class="data-label">Número da Credencial</label>
                    <span class="data-value">{{ credential.credentialNumber | mask : '9999999/9999' }}</span>
                  </div>
                  <div class="data-field">
                    <label class="data-label">Tipo de Credencial</label>
                    <span class="data-value">{{ credential.type }}</span>
                  </div>
                </div>

                <div class="data-row">
                  <div class="data-field">
                    <label class="data-label">Número do Processo</label>
                    <span class="data-value">{{ credential.processNumber | mask : 'A999999/9999' }}</span>
                  </div>
                  <div class="data-field">
                    <label class="data-label">Status da Credencial</label>
                    <span class="data-value">{{ credential.status }}</span>
                  </div>
                </div>

                <div class="data-row">
                  <div class="data-field">
                    <label class="data-label">Data da Emissão</label>
                    <span class="data-value">{{ credential.createdAt | date : 'd/M/yyyy H:m' }}</span>
                  </div>
                  <div class="data-field">
                    <label class="data-label">Validade</label>
                    <span class="data-value">{{
                      credential.expiringDate > '2124-01-01'
                        ? 'Não Expira'
                        : (credential.expiringDate | date : 'd/M/yyyy')
                    }}</span>
                  </div>
                </div>

                <div class="data-row">
                  <div class="data-field">
                    <label class="data-label">CPF do Beneficiário</label>
                    <div class="data-value-with-action">
                      <span>{{ credential.recipientSummary.cpf | mask : '999.999.999-99' }}</span>
                      <button
                        type="button"
                        class="icon-button copy-button"
                        (click)="copyToClipboard(credential.recipientSummary.cpf)"
                        pTooltip="Copiar CPF"
                        title="Copiar CPF">
                        <i class="pi pi-copy"></i>
                      </button>
                      <ng-container *ngIf="isUser()">
                        <button
                          type="button"
                          class="icon-button credential-button"
                          (click)="selectCpfForCredential(credential.recipientSummary.cpf)"
                          pTooltip="Emitir Credencial"
                          title="Emitir Credencial">
                          <i class="pi pi-id-card"></i>
                        </button>
                      </ng-container>
                    </div>
                  </div>
                  <div class="data-field">
                    <label class="data-label">Nome do Beneficiário</label>
                    <span class="data-value">{{ credential.recipientSummary.name }}</span>
                  </div>
                </div>

                <div class="data-row">
                  <div class="data-field">
                    <label class="data-label">CPF do Solicitante</label>
                    <span class="data-value">{{ credential.applicantSummary.cpf | mask : '999.999.999-99' }}</span>
                  </div>
                  <div class="data-field">
                    <label class="data-label">Nome do Solicitante</label>
                    <span class="data-value">{{ credential.applicantSummary.name }}</span>
                  </div>
                </div>

                <div class="data-row">
                  <div class="data-field full-width">
                    <label class="data-label">Observação</label>
                    <span class="data-value">{{ credential.observation }}</span>
                  </div>
                </div>

                <div class="data-row">
                  <div class="data-field">
                    <label class="data-label">CPF do Responsável</label>
                    <span class="data-value">{{ credential.userSummary.login | partialCpfMask : 3 : 2 }}</span>
                  </div>
                  <div class="data-field">
                    <label class="data-label">Nome do Responsável</label>
                    <span class="data-value">{{ credential.userSummary.username }}</span>
                  </div>
                </div>
              </div>
            </div>
          </p-panel>
          } @else if (!isLoading) {
          <p-panel header="Resultado" [toggleable]="false" styleClass="compact-panel">
            <div class="no-data">
              <i class="pi pi-info-circle"></i>
              <span>Dados da Credencial não disponíveis.</span>
            </div>
          </p-panel>
          }
        </form>
      </div>
    </p-card>
  `,
  styles: `
    /* Card Header */
    .card-header {
      padding: 16px 20px;
      margin: 0;
      background: rgba(255, 230, 203, 0.1);
      border-bottom: 1px solid #FFE6CB;
    }

    .card-header h2 {
      color: #325565;
      font-weight: 600;
      font-size: 1.5rem;
      margin: 0;
      font-family: "Montserrat", sans-serif;
    }

    /* Container Principal */
    .credential-container {
      padding: 10px;
    }

    .credential-form {
      width: 100%;
    }

    /* Panels Compactos */
    :host ::ng-deep .compact-panel {
      margin-bottom: 1rem;
    }

    :host ::ng-deep .compact-panel .p-panel-header {
      background: rgba(255, 230, 203, 0.1);
      border: 1px solid #FFE6CB;
      color: #325565;
      font-weight: 600;
      padding: 0.75rem 1rem;
    }

    :host ::ng-deep .compact-panel .p-panel-content {
      padding: 1rem;
      border: 1px solid #FFE6CB;
      border-top: none;
    }

    /* Grid de Formulário */
    .form-grid {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .form-row {
      display: grid;
      grid-template-columns: 1fr 1fr 2fr;
      gap: 0.5rem;
      align-items: end;
    }

    .form-field {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    .form-field:first-child {
      max-width: 300px;
    }

    .field-label {
      font-weight: 600;
      color: #325565;
      font-size: 0.875rem;
      margin-bottom: 0.25rem;
    }

    /* Inputs Compactos */
    :host ::ng-deep .compact-input {
      height: 2.5rem !important;
      font-size: 0.875rem;
    }

    :host ::ng-deep .compact-input .p-inputtext {
      height: 2.5rem !important;
      font-size: 0.875rem;
      padding: 0.5rem;
    }

    /* Botão de Busca */
    .search-button {
      background-color: #F15A22;
      color: white;
      border: none;
      padding: 0.5rem 1rem;
      border-radius: 6px;
      font-weight: 500;
      transition: background-color 0.2s;
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      cursor: pointer;
      min-height: 2.5rem;
    }

    .search-button:hover:not(:disabled) {
      background-color: #E55A2B;
    }

    .search-button:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .search-button .pi-spin {
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    .search-button .p-button-label {
      font-size: 1rem;
    }

    /* Button Group */
    .button-group {
      display: flex;
      gap: 0.5rem;
      align-items: center;
    }

    /* Botão Limpar */
    .clear-button {
      background: none;
      border: none;
      color: #6c757d;
      cursor: pointer;
      padding: 0.5rem;
      border-radius: 50%;
      transition: all 0.2s;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2.5rem;
      height: 2.5rem;
      font-size: 1.2rem;
    }

    .clear-button:hover {
      color: #495057;
      background-color: rgba(108, 117, 125, 0.1);
    }

    .clear-button i {
      font-family: 'primeicons' !important;
    }

    /* Dados da Credencial */
    .credential-data {
      width: 100%;
    }

    .data-grid {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .data-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
      padding: 0.75rem 0;
      border-bottom: 1px solid #e9ecef;
    }

    .data-field {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    .data-field.full-width {
      grid-column: 1 / -1;
    }

    .data-label {
      font-weight: 600;
      color: #325565;
      font-size: 0.875rem;
    }

    .data-value {
      color: #495057;
      font-size: 0.875rem;
      word-break: break-word;
    }

    .data-value-with-action {
      display: flex;
      color: #495057;
      font-size: 0.875rem;
      align-items: center;
      gap: 0.5rem;
    }

    /* Botões de Ícone - Base */
    .icon-button {
      background: none;
      border: none;
      cursor: pointer;
      padding: 0.5rem;
      border-radius: 50%;
      transition: all 0.2s;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2.5rem;
      height: 2.5rem;
      font-size: 1.2rem;
    }

    .icon-button:hover {
      transform: scale(1.1);
    }

    .icon-button:active {
      transform: scale(0.95);
    }

    .icon-button i {
      font-family: 'primeicons' !important;
      display: inline-block;
      line-height: 1;
    }

    /* Botão de Copiar */
    .copy-button {
      color: #F15A22 !important;
    }

    .copy-button:hover {
      background-color: rgba(241, 90, 34, 0.1) !important;
    }

    /* Garantir que os ícones do PrimeNG sejam renderizados */
    :host ::ng-deep .copy-button .p-button-icon,
    :host ::ng-deep .credential-button .p-button-icon {
      font-family: 'primeicons' !important;
      font-size: 1.2rem;
      line-height: 1;
    }

    /* Botão de Credencial */
    .credential-button {
      color: rgb(16, 148, 158) !important;
    }

    .credential-button:hover {
      background-color: rgba(16, 148, 158, 0.1) !important;
    }

    /* Mensagem de Nenhum Dado */
    .no-data {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      padding: 2rem;
      color: #6c757d;
      background-color: #f8f9fa;
      border-radius: 8px;
      border: 1px solid #e9ecef;
    }

    .no-data i {
      font-size: 1.5rem;
      color: #F15A22;
    }

    .no-data span {
      font-size: 1rem;
      font-style: italic;
    }

    /* Validação */
    :host ::ng-deep .p-invalid {
      border-color: #dc3545 !important;
      background-color: rgba(220, 53, 69, 0.1) !important;
    }

    :host ::ng-deep .p-error {
      color: #dc3545;
      font-size: 0.75rem;
      margin-top: 0.25rem;
    }

    /* PrimeNG Card Customization */
    :host ::ng-deep .p-card {
      border: none;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      margin: 20px;
    }

    :host ::ng-deep .p-card .p-card-body {
      padding: 0;
    }

    :host ::ng-deep .p-card .p-card-content {
      padding: 20px;
    }

    /* Responsive Design */
    @media (max-width: 768px) {
      .form-row {
        grid-template-columns: 1fr;
        gap: 0.75rem;
      }

      .data-row {
        grid-template-columns: 1fr;
        gap: 0.5rem;
      }

      .card-header h2 {
        font-size: 1.25rem;
      }

      .data-field.full-width {
        grid-column: auto;
      }
    }
  `,
})
export class CredentialComponent implements OnInit, AfterViewInit, OnDestroy {
  credentialForm!: FormGroup<CredentialForm>;
  credential: Credential | null = null;
  applicant: any = null;
  isLoading: boolean = false;

  private _destroy$ = new Subject<void>();

  @ViewChild('credentialNumber', { static: false }) credentialNumberInputField: any;

  constructor(
    private _formBuilder: FormBuilder,
    private credentialsService: CredentialsService,
    private router: Router,
    private messageService: MessageService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private clipboard: Clipboard,
    private renderer: Renderer2,
    private cpfSharedService: CpfSharedService,
    private credentialsStateService: CredentialsStateService
  ) {}

  ngOnInit(): void {
    this.credentialForm = this._formBuilder.group({
      credentialNumber: new FormControl('', [
        // Removendo validação obrigatória para permitir busca com valores parciais
        // Validators.required,
        Validators.pattern(/^(\d{7}\/\d{4}|\d{11})$/),
      ]),
    });

    // Escuta por número de credencial compartilhado
    this.credentialsStateService.credentialNumber$
      .pipe(takeUntil(this._destroy$))
      .subscribe(credentialNumber => {
        if (credentialNumber) {
          // Formatar com barra se necessário
          const formatted = this.formatCredentialNumber(credentialNumber);
          this.credentialForm.patchValue({ credentialNumber: formatted });
          this.cdr.detectChanges();
          
          // Disparar busca automaticamente após um pequeno delay
          setTimeout(() => {
            this.onSubmitCredentialNumber();
            // Limpar o valor do serviço após usar
            this.credentialsStateService.clearCredentialNumber();
          }, 100);
        }
      });
  }

  ngAfterViewInit(): void {
    // Múltiplas tentativas para focar no campo devido ao tempo de renderização do PrimeNG
    this.attemptFocus(0);
  }

  private attemptFocus(attempt: number): void {
    if (attempt >= 5) { // Máximo de 5 tentativas
      // console.log('Falhou em focar após 5 tentativas');
      return;
    }

    const delay = attempt === 0 ? 100 : attempt * 200; // 100ms, 200ms, 400ms, 600ms, 800ms

    setTimeout(() => {
      // console.log(`Tentativa ${attempt + 1} de focar no campo de credencial...`);

      // Abordagem direta: usar document.querySelector com o ID do input
      const inputElement = document.getElementById('credentialNumberInput') as HTMLInputElement;

      if (inputElement) {
        // console.log('Input encontrado via getElementById:', inputElement);
        // Usar Renderer2 para focar
        this.renderer.selectRootElement(inputElement).focus();
        // Garantir que o cursor esteja no final
        if (inputElement.setSelectionRange) {
          inputElement.setSelectionRange(inputElement.value.length, inputElement.value.length);
        }
        // console.log('Foco aplicado com sucesso!');
        return; // Sucesso, não tentar mais
      }

      // Fallback: tentar outros seletores
      let fallbackInput = document.querySelector('p-inputMask input') as HTMLInputElement;
      if (!fallbackInput) {
        fallbackInput = document.querySelector('.p-inputmask input') as HTMLInputElement;
      }
      if (!fallbackInput) {
        const allInputs = document.querySelectorAll('input');
        for (let i = 0; i < allInputs.length; i++) {
          if (allInputs[i].placeholder === '0000000/0000') {
            fallbackInput = allInputs[i] as HTMLInputElement;
            break;
          }
        }
      }

      if (fallbackInput) {
        // console.log('Input encontrado via fallback:', fallbackInput);
        this.renderer.selectRootElement(fallbackInput).focus();
        if (fallbackInput.setSelectionRange) {
          fallbackInput.setSelectionRange(fallbackInput.value.length, fallbackInput.value.length);
        }
        // console.log('Foco aplicado via fallback!');
        return;
      }

      // console.log('Nenhum input encontrado na tentativa', attempt + 1);

      // Se chegou aqui, tentar novamente
      this.attemptFocus(attempt + 1);
    }, delay);
  }

  onSubmitCredentialNumber(): void {
    // Garantir que pegamos o valor mais recente do input
    const inputElement = this.credentialNumberInputField?.nativeElement;
    const inputValue = inputElement?.value || '';
    const formValue = this.credentialForm.get('credentialNumber')!.value;

    // Usar o valor do input se disponível, senão o do formControl
    const credentialValue = inputValue || formValue;

    // console.log('Valor do input:', inputValue);
    // console.log('Valor do formulário:', formValue);
    // console.log('Valor usado:', credentialValue);

    if (credentialValue && credentialValue.trim()) {
      // Validar formato básico antes de enviar
      const cleanValue = credentialValue.replace(/\D/g, '');
      if (cleanValue.length < 7) {
        this.showSnackBar('Número da credencial deve ter pelo menos 7 dígitos.');
        this.credentialForm.get('credentialNumber')?.markAsTouched();
        return;
      }

      this.isLoading = true;
      // Remover a barra '/' antes de enviar para a API
      const credentialValueForApi = credentialValue.trim().replace(/\//g, '');
      // console.log('Fazendo busca para:', credentialValue);
      // console.log('Valor enviado para API:', credentialValueForApi);

      this.credentialsService
        .getCredentialDataByCredentialNumber(credentialValueForApi)
        .pipe(
          takeUntil(this._destroy$),
          finalize(() => {
            this.isLoading = false;
            this.cdr.detectChanges();
          })
        )
        .subscribe({
          next: (response) => {
            if (response) {
              this.credential = response;
              this.showSnackBar('Credencial encontrada com sucesso!');
            } else {
              this.credential = null;
              this.showSnackBar('Nenhum dado encontrado.');
            }
          },
          error: (error) => {
            console.error('Erro na busca:', error);
            this.credential = null;
            
            if (error.status === 404) {
              this.showSnackBar('A Credencial Informada não existe!');
            } else if (error.status === 400) {
              this.showSnackBar('Formato da credencial inválido!');
            } else {
              this.showSnackBar('Erro ao buscar Credencial. Tente novamente.');
            }
          },
        });
    } else {
      // console.log('Nenhum valor fornecido');
      this.showSnackBar('Por favor, informe o número da credencial.');
      this.credentialForm.get('credentialNumber')?.markAsTouched();
    }
  }

  private showSnackBar(message: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: message,
      life: 3000
    });
  }

  private formatCredentialNumber(credentialNumber: string): string {
    // Remove qualquer formatação existente
    const clean = credentialNumber.replace(/\D/g, '');
    
    // Se tiver 11 dígitos, formata como 9999999/9999
    if (clean.length === 11) {
      return `${clean.substring(0, 7)}/${clean.substring(7)}`;
    }
    
    return clean;
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.credentialForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  onCredentialInputComplete(event: any): void {
    // Garantir que o valor seja atualizado no formControl
    const value = event.value || event.target?.value;
    if (value) {
      this.credentialForm.get('credentialNumber')?.setValue(value);
    }
  }

  onCredentialInput(event: any): void {
    // Capturar mudanças em tempo real no input
    const value = event.target?.value;
    if (value !== undefined) {
      this.credentialForm.get('credentialNumber')?.setValue(value);
    }
  }

  clearSearch(): void {
    this.credentialForm.reset();
    this.credential = null;
    this.isLoading = false;

    // Focar no campo após limpar
    setTimeout(() => {
      this.attemptFocus(0);
    }, 0);
  }

  copyToClipboard(cpf: string): void {
    // Remove qualquer formatação do CPF (pontos e traços)
    const cleanCpf = cpf.replace(/\D/g, '');

    // Copia para a área de transferência
    this.clipboard.copy(cleanCpf);

    // Exibe mensagem de confirmação
    this.showSnackBar('CPF copiado para a área de transferência!');
  }

  selectCpfForCredential(cpf: string): void {
    // Remove qualquer formatação do CPF (pontos e traços)
    const cleanCpf = cpf.replace(/\D/g, '');

    // Define o CPF no serviço compartilhado como beneficiário
    this.cpfSharedService.setRecipientCpf(cleanCpf);
    this.cpfSharedService.setCurrentPerson('recipient');
    this.cpfSharedService.setToggleValue(true);

    // Exibe mensagem de confirmação
    this.showSnackBar('Redirecionando para emissão de credencial...');

    // Navega para a página de emissão
    setTimeout(() => {
      this.router.navigate(['/home/emission']);
    }, 500);
  }

    isUser(): boolean {
    const roles = JSON.parse(sessionStorage.getItem('roles') || '[]');
    return roles.includes('ROLE_USER');
  }
}
